'use client';

import classNames from 'classnames';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { FC } from 'react';
import RiHome2Fill from '~icons/ri/home-2-fill';
import RiMusic2Fill from '~icons/ri/music-2-fill';
import RiGamepadFill from '~icons/ri/gamepad-fill';
import RiTrophyFill from '~icons/ri/trophy-fill';
import RiArrowRightLine from '~icons/ri/arrow-right-line';
import RiLoader5Fill from '~icons/ri/loader-5-fill';
import useSWR from 'swr';
import { ibm } from '@/utils/fonts/fonts';

interface Props {
  className?: string;
}

export const TopNavigation: FC<Props> = props => {
  const pathname = usePathname();

  const { data, error } = useSWR<boolean>(
    [process.env['API_URL'] + 'leaderboards/onlinestatus'],
    async (input: RequestInfo, init: RequestInit) => {
      const res = await fetch(input, init);
      return res.json();
    },
    { refreshInterval: 60 * 1000 }
  );

  return (
    <section
      style={ibm.style}
      className={classNames(
        'relative mx-auto flex h-16 w-full max-w-7xl flex-row items-center justify-between gap-3 overflow-hidden rounded-full bg-[#151515]/80 p-4 py-3 text-sm backdrop-blur-3xl backdrop-brightness-150',
        props.className
      )}
    >
      <nav className="hidden flex-row items-center gap-3 md:flex">
        <Link
          href="/"
          className={classNames(
            'rounded-full p-2 px-3 transition-all duration-200 ease-in-out hover:bg-zinc-800',
            { 'bg-zinc-800': pathname === '/', '': pathname !== '/' }
          )}
        >
          Главная
        </Link>

        <Link
          href="/streamed-music"
          className={classNames(
            'rounded-full p-2 px-5 transition-all duration-200 ease-in-out hover:bg-zinc-800',
            {
              'bg-zinc-800': pathname === '/streamed-music',
              '': pathname !== '/streamed-music',
            }
          )}
        >
          Музыка со стримов
        </Link>

        <Link
          href="/game-reviews"
          className={classNames(
            'rounded-full p-2 px-5 transition-all duration-200 ease-in-out hover:bg-zinc-800',
            {
              'bg-zinc-800': pathname.includes('/game-reviews'),
              '': !pathname.includes('/game-reviews'),
            }
          )}
        >
          Игровые рецензии
        </Link>

        <Link
          href="/leaderboard"
          className={classNames(
            'rounded-full p-2 px-5 transition-all duration-200 ease-in-out hover:bg-zinc-800',
            {
              'bg-zinc-800': pathname === '/leaderboard',
              '': pathname !== '/leaderboard',
            }
          )}
        >
          Рейтинг
        </Link>
      </nav>

      <nav className="justify mx-4 flex w-full flex-row items-center justify-between text-lg md:hidden">
        <Link
          href="/"
          className={classNames('flex flex-col items-center', {
            '': pathname === '/',
            'opacity-50': pathname !== '/',
          })}
        >
          <RiHome2Fill />
          <p className="text-xs">Главная</p>
        </Link>

        <Link
          href="/streamed-music"
          className={classNames('flex flex-col items-center', {
            '': pathname === '/streamed-music',
            'opacity-50': pathname !== '/streamed-music',
          })}
        >
          <RiMusic2Fill />
          <p className="text-xs">Музыка</p>
        </Link>

        <Link
          href="/game-reviews"
          className={classNames('flex flex-col items-center', {
            '': pathname.includes('/game-reviews'),
            'opacity-50': !pathname.includes('/game-reviews'),
          })}
        >
          <RiGamepadFill />
          <p className="text-xs">Игры</p>
        </Link>

        <Link
          href="/leaderboard"
          className={classNames('flex flex-col items-center', {
            '': pathname === '/leaderboard',
            'opacity-50': pathname !== '/leaderboard',
          })}
        >
          <RiTrophyFill />
          <p className="text-xs">Рейтинг</p>
        </Link>
      </nav>

      <section className="hidden md:flex">
        {data === undefined ? (
          <div className="mx-6 py-2">
            <RiLoader5Fill className="animate-spin text-lg" />
          </div>
        ) : (
          <Link
            href={
              data === true
                ? 'https://www.twitch.tv/tijoe'
                : 'https://t.me/tijoestream'
            }
            target="_blank"
            className={classNames(
              'group flex flex-row items-center gap-3 overflow-hidden rounded-full px-3 py-2 transition-all duration-200 ease-out hover:bg-zinc-800'
            )}
          >
            <div className="flex translate-x-3.5 flex-row items-center gap-2 transition-all duration-200 ease-in-out group-hover:-translate-x-6">
              {data === true ? (
                <div className="flex h-5 w-5 animate-pulse items-center justify-center rounded-full bg-emerald-600/20 shadow-2xl shadow-emerald-500 transition-all duration-200 ease-in-out group-hover:invisible">
                  <div className="h-2 w-2 animate-pulse rounded-full bg-emerald-600 shadow-emerald-500" />
                </div>
              ) : (
                <div className="flex h-5 w-5 animate-pulse items-center justify-center rounded-full bg-rose-600/20 shadow-2xl shadow-rose-500 transition-all duration-200 ease-in-out group-hover:invisible">
                  <div className="h-2 w-2 animate-pulse rounded-full bg-rose-600 shadow-rose-500" />
                </div>
              )}
              <span>{data === true ? 'стрим онлайн' : 'стрим оффлайн'}</span>
            </div>
            <RiArrowRightLine className="mr-1 translate-x-10 text-sm transition-all duration-200 ease-in-out group-hover:translate-x-0" />
          </Link>
        )}
      </section>
    </section>
  );
};
